/**
 * Walking a collection end to end, across its sections.
 *
 * A collection is sections of studies, but a coach reading it is at study
 * 9 of 15, not at "section 4, item 2". Next at the end of a section has to
 * land on the first study of the next one. Nothing in the app does that
 * today: the masterclass reader walks one flat chapter list, the cert
 * walks within a unit and stops dead at its edge, and DrillStack takes a
 * flat array.
 *
 * So the sections get flattened once into a single indexed list, each
 * entry remembering which section it came from. Previous and Next then
 * become index arithmetic, section boundaries stop being a special case,
 * and DrillStack can drive the whole thing unchanged.
 *
 * Pure, and separate from the page, because this is the part with an
 * off-by-one in it.
 */

/**
 * flattenStudies — sections in, one ordered list out.
 *
 * Each entry carries its study plus where it sits: the section's number,
 * title and note, its position within that section, and its position in
 * the collection as a whole. The page needs all of it to render a heading
 * above the card without looking anything up again.
 *
 * Sections with no studies are skipped rather than counted. An empty
 * section is a content state, not a reading step, and letting one through
 * would leave a coach on a numbered page with nothing on it.
 */
export function flattenStudies(sections) {
  const list = Array.isArray(sections) ? sections : [];
  const out = [];
  list.forEach((section) => {
    const studies = Array.isArray(section?.studies) ? section.studies : [];
    studies.forEach((study, withinSection) => {
      out.push({
        study,
        sectionNumber: section.number,
        sectionTitle: section.title,
        sectionSummary: section.summary,
        sectionNote: section.note,
        indexInSection: withinSection,
        studiesInSection: studies.length,
        index: out.length,
      });
    });
  });
  return out.map((entry) => ({ ...entry, total: out.length }));
}

/**
 * clampIndex — a URL is user input, and /99 should not render "Study 100 of 15".
 *
 * Out of range in either direction, or not a number at all, lands on the
 * first study. Deliberately not the nearest edge: arriving at the last
 * study because you mistyped the URL reads as "you finished", which is a
 * worse lie than "you started".
 */
export function clampIndex(raw, total) {
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return 0;
  if (n < 0 || n >= total) return 0;
  return n;
}

/** 1-based for the URL and the counter, 0-based everywhere inside. */
export const toDisplay = (index) => index + 1;
export const fromDisplay = (display) => Number(display) - 1;

/**
 * step — move, and refuse to wrap.
 *
 * A collection is an argument with an order, so running off the end
 * should stop rather than loop back to the beginning. The page uses the
 * returned index being unchanged to know to disable the button.
 */
export function step(index, delta, total) {
  if (!total) return 0;
  const next = index + delta;
  if (next < 0 || next >= total) return index;
  return next;
}

export const canGoBack = (index) => index > 0;
export const canGoForward = (index, total) => index < total - 1;

/**
 * sectionProgress — "2 of 3 in this section".
 *
 * The overall counter says how far through the collection you are, which
 * is the number a coach wants when deciding whether to keep going. This
 * is the one that says whether the current idea is nearly finished, which
 * is the number they want when deciding whether to stop.
 */
export function sectionProgress(entry) {
  if (!entry) return null;
  return { at: entry.indexInSection + 1, of: entry.studiesInSection };
}

/**
 * Where a study lives, for a link from the collection landing page.
 * One place that knows the URL shape, so the three pages cannot disagree.
 */
export const studyHref = (slug, index) => `#/research/${slug}/${toDisplay(index)}`;
export const collectionHref = (slug) => `#/research/${slug}`;
export const COLLECTIONS_HREF = "#/research";

/**
 * parseResearchRoute — read the hash the three pages share.
 *
 * `nav` arrives already stripped of "#/" by BAMFull's parseHash, so this
 * takes "research", "research/<slug>" or "research/<slug>/<n>" and says
 * which of the three pages is being asked for.
 */
export function parseResearchRoute(nav) {
  const parts = String(nav || "").split("/").filter(Boolean);
  if (parts[0] !== "research") return null;
  if (parts.length === 1) return { view: "index" };
  if (parts.length === 2) return { view: "collection", slug: parts[1] };
  return { view: "study", slug: parts[1], displayIndex: parts[2] };
}
